import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { Icon, Swatch } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { useScans } from "@/hooks/useFigData";
import { supabase } from "@/integrations/supabase/client";
import { scanStore, type AIScan } from "@/lib/scanStore";
import type { SwatchKind } from "@/components/primitives";

// The Ledger = full history of every scan you've ever made. Same closet
// rules as /you (real scans only, no mock sales record), but surfaced as
// a searchable archive with the four sort modes.

type SortKey = "newest" | "oldest" | "category" | "status";
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "newest",   label: "Newest" },
  { key: "oldest",   label: "Oldest" },
  { key: "category", label: "Item type" },
  { key: "status",   label: "Status" },
];

interface ClosetItem {
  id: string;
  img: SwatchKind;
  title: string;
  brand: string;
  era: string;
  category: string;
  price: number | null;
  verdict: string;
  posted: boolean;
  platform: string | null;   // null = kept in closet
  reviewStatus: string;
  created_at: string;
  photoUrl: string | null;
  searchHaystack: string;    // pre-built lowercase string for fast filtering
}

export default function History() {
  const nav = useNavigate();
  const { scans } = useScans();
  const [sortBy, setSortBy] = useState<SortKey>("newest");
  const [query, setQuery] = useState("");
  const [opening, setOpening] = useState<string | null>(null);

  // Normalise every scan into a ClosetItem.
  const allItems: ClosetItem[] = useMemo(() => {
    return scans.map((s) => {
      const postedTo = (s as { posted_to?: string | null }).posted_to ?? null;
      const legacyMatch = s.posted ? s.status?.match(/^(?:Listed|Sold) · (.+)$/) : null;
      const legacyPlatform = legacyMatch ? legacyMatch[1] : null;
      const platform = postedTo ?? legacyPlatform;
      const photos = (s as { photos?: string[] }).photos ?? [];
      const photo = photos[0] ?? null;
      const isUrl = typeof photo === "string" && /^https?:\/\//.test(photo);
      const brand = s.brand ?? "";
      const haystack = [s.title, brand, s.era, s.category, ...(s.tags ?? [])]
        .filter(Boolean).join(" ").toLowerCase();
      return {
        id: s.id,
        img: (s.img as SwatchKind) || "olive",
        title: s.title,
        brand,
        era: s.era ?? "—",
        category: s.category ?? "Other",
        price: s.price_suggested != null ? Number(s.price_suggested) : null,
        verdict: s.verdict,
        posted: s.posted,
        platform,
        reviewStatus: ((s as { review_status?: string }).review_status ?? "pending") as string,
        created_at: s.created_at ?? new Date().toISOString(),
        photoUrl: isUrl ? photo : null,
        searchHaystack: haystack,
      };
    });
  }, [scans]);

  // Apply search first, then sort.
  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const arr = q
      ? allItems.filter((it) => it.searchHaystack.includes(q))
      : [...allItems];

    switch (sortBy) {
      case "newest":
        return arr.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
      case "oldest":
        return arr.sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
      case "category":
        return arr.sort((a, b) => a.category.localeCompare(b.category) || a.title.localeCompare(b.title));
      case "status": {
        const rank = (it: ClosetItem) => it.platform ? 1 : 0;
        return arr.sort((a, b) => {
          if (rank(a) !== rank(b)) return rank(a) - rank(b);
          if (a.platform && b.platform) return a.platform.localeCompare(b.platform);
          return +new Date(b.created_at) - +new Date(a.created_at);
        });
      }
    }
  }, [allItems, query, sortBy]);

  // Quick stats — replaces the mock sales record. All real, derived from scans.
  const stats = useMemo(() => {
    const total = allItems.length;
    const kept = allItems.filter((it) => !it.platform).length;
    const listed = allItems.filter((it) => !!it.platform).length;
    return { total, kept, listed };
  }, [allItems]);

  // Same item-open flow as Profile: rehydrate scanStore from the DB row,
  // then route to /result.
  const openItem = async (id: string) => {
    if (opening) return;
    setOpening(id);
    try {
      const { data, error } = await supabase
        .from("scans")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("Item not found");

      const ai: AIScan = {
        title: data.title,
        brand: data.brand ?? "",
        era: data.era ?? "",
        origin: "",
        category: data.category ?? "",
        material: data.material ?? "",
        size: data.size ?? "",
        color: data.color ?? "",
        condition: data.condition ?? "",
        condition_notes: data.condition_notes ?? "",
        verdict: (data.verdict as "PASS" | "FAIL") ?? "PASS",
        verdict_label: data.verdict_label ?? "",
        confidence: data.confidence ?? 0,
        rarity: data.rarity ?? "",
        tags: data.tags ?? [],
        notes: data.notes ?? [],
        forensic: [],
        price_low: data.price_low != null ? Number(data.price_low) : 0,
        price_suggested: data.price_suggested != null ? Number(data.price_suggested) : 0,
        price_high: data.price_high != null ? Number(data.price_high) : 0,
      };
      scanStore.setResult(ai);
      scanStore.setPhotos(data.photos ?? []);
      sessionStorage.setItem("fig_current_scan_id", data.id);
      sessionStorage.setItem("fig_scan_saved_id", data.id);
      nav("/result");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't open item";
      toast.error(msg);
    } finally {
      setOpening(null);
    }
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        {/* Header */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">The Ledger</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Title + stats — real data only */}
        <div style={{ padding: "10px 20px 0" }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Your archive</span>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 44,
            letterSpacing: "-0.03em", lineHeight: 0.95, marginTop: 4, color: "hsl(var(--ink))",
          }}>
            {stats.total}
            <span style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 400,
              fontSize: 18, color: "hsl(var(--ink-2))",
            }}> · {stats.total === 1 ? "scan" : "scans"}</span>
          </div>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 4,
          }}>
            <span style={{ color: "hsl(var(--olive))" }}>{stats.kept} kept</span>
            {" · "}
            <span style={{ color: "hsl(var(--rust))" }}>{stats.listed} listed</span>
          </div>
        </div>

        {/* Search bar */}
        <div style={{ padding: "16px 20px 0" }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, brand, era, tag…"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid hsl(var(--ink))",
              background: "hsl(var(--cream))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, letterSpacing: "0.05em",
              color: "hsl(var(--ink))",
              outline: "none",
            }}
          />
        </div>

        {/* Sort chips */}
        {allItems.length > 0 && (
          <div style={{ padding: "10px 20px 0", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {SORT_OPTIONS.map((opt) => {
              const active = sortBy === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  style={{
                    padding: "5px 10px",
                    border: "1px solid hsl(var(--ink))",
                    background: active ? "hsl(var(--ink))" : "hsl(var(--cream))",
                    color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Result count */}
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>
            {filteredSorted.length} {filteredSorted.length === 1 ? "result" : "results"}
            {query && ` for "${query}"`}
          </span>
        </div>

        {/* List */}
        <div style={{ padding: "10px 20px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          {filteredSorted.length === 0 && (
            <div style={{
              padding: "24px 16px", border: "1px dashed hsl(var(--ink-2))",
              textAlign: "center",
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              color: "hsl(var(--ink-2))",
            }}>
              {allItems.length === 0
                ? "No scans yet. Scan something to start your archive."
                : "Nothing matches that search."}
            </div>
          )}

          {filteredSorted.map((it) => {
            const isLoading = opening === it.id;
            // Review-status badge mirrors the To Review queue states. Falls
            // back to the legacy Listed/Kept labels for older rows where
            // review_status was never set (treated as approved_closet).
            const rs = it.reviewStatus;
            let statusLabel: string;
            let statusColor: string;
            if (rs === "pending") {
              statusLabel = "Awaiting review";
              statusColor = "hsl(var(--rust))";
            } else if (rs === "discarded") {
              statusLabel = "Discarded";
              statusColor = "hsl(var(--ink-2))";
            } else if (rs === "approved_feed" || it.posted) {
              statusLabel = it.platform
                ? `Posted · ${it.platform.charAt(0).toUpperCase() + it.platform.slice(1).toLowerCase()}`
                : "Posted";
              statusColor = "hsl(var(--rust))";
            } else {
              statusLabel = "In closet";
              statusColor = "hsl(var(--olive))";
            }
            const dateStr = new Date(it.created_at).toLocaleDateString(undefined, {
              month: "short", day: "numeric", year: "numeric",
            });

            return (
              <button
                key={it.id}
                onClick={() => openItem(it.id)}
                disabled={isLoading}
                style={{
                  display: "flex", gap: 12, padding: 0,
                  background: "hsl(var(--cream))",
                  border: "1px solid hsl(var(--ink))",
                  cursor: isLoading ? "wait" : "pointer",
                  textAlign: "left",
                  opacity: isLoading ? 0.6 : 1,
                  width: "100%",
                  alignItems: "stretch",
                }}
              >
                {/* Thumbnail */}
                <div style={{ width: 84, flexShrink: 0, position: "relative", borderRight: "1px solid hsl(var(--ink))" }}>
                  {it.photoUrl ? (
                    <img
                      src={it.photoUrl}
                      alt={it.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", minHeight: 84 }}
                    />
                  ) : (
                    <Swatch kind={it.img} style={{ width: "100%", height: "100%", minHeight: 84 }} />
                  )}
                  {it.verdict === "FAIL" && (
                    <div style={{
                      position: "absolute", top: 4, right: 4,
                      background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
                      letterSpacing: "0.08em", padding: "2px 4px",
                    }}>FAIL</div>
                  )}
                </div>

                {/* Body */}
                <div style={{ flex: 1, minWidth: 0, padding: "10px 12px 10px 0", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    <div className="caps" style={{ color: statusColor, fontSize: 9 }}>
                      {statusLabel}
                    </div>
                    <div style={{
                      fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 16,
                      color: "hsl(var(--ink))", lineHeight: 1.15, marginTop: 2,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {it.title}
                    </div>
                    <div className="caps" style={{ marginTop: 3, color: "hsl(var(--ink-2))", fontSize: 9 }}>
                      {it.era}{it.category && it.category !== "Other" ? ` · ${it.category}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6, gap: 8 }}>
                    <span className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 9 }}>
                      {dateStr}
                    </span>
                    {it.price != null && (
                      <span style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 16,
                        color: "hsl(var(--ink))",
                      }}>
                        ${it.price}
                      </span>
                    )}
                  </div>
                </div>

                {/* Chev */}
                <div style={{ display: "flex", alignItems: "center", padding: "0 10px", color: "hsl(var(--ink-2))" }}>
                  {Icon.chev(14)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </MobileFrame>
  );
}
